import {
  onAuthStateChanged as _onAuthStateChanged,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  OAuthProvider,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  type UserCredential,
} from "firebase/auth";
import type { ArrayTail } from "type-fest";

import { requestMagicLink, type SendEmailPayload } from "./api";
import { getFirebaseAuth } from "./firebase";

const STORAGE_KEY = "monthly-email_for_sign_in";

export async function signInWithGoogle(): Promise<UserCredential> {
  const auth = getFirebaseAuth();
  auth.useDeviceLanguage();

  const provider = new GoogleAuthProvider();

  try {
    const result = await signInWithPopup(auth, provider);
    return result;
  } catch (error: any) {
    console.error("Error signing in with Google:", error);
    throw new Error(error.message || "Failed to sign in with Google");
  }
}

export async function signInWithMicrosoft(): Promise<UserCredential> {
  const auth = getFirebaseAuth();
  auth.useDeviceLanguage();

  const provider = new OAuthProvider("microsoft.com");

  try {
    const result = await signInWithPopup(auth, provider);
    return result;
  } catch (error: any) {
    console.error("Error signing in with Microsoft:", error);
    throw new Error(error.message || "Failed to sign in with Microsoft");
  }
}

export async function sendMagicLink(
  email: string,
  type: SendEmailPayload["type"],
): Promise<void> {
  try {
    await requestMagicLink(
      email,
      `${window.location.origin}/auth/verify`,
      type,
    );
    window.localStorage.setItem(STORAGE_KEY, email);
  } catch (error: any) {
    console.error("Error sending magic link:", error);
    throw error;
  }
}

export async function completeMagicLinkSignIn(
  emailLink: string,
): Promise<UserCredential> {
  const auth = getFirebaseAuth();

  if (!isSignInWithEmailLink(auth, emailLink)) {
    throw new Error("Invalid sign-in link");
  }

  // Try to get email from storage (same device flow)
  let email = window.localStorage.getItem(STORAGE_KEY);

  // If no email in storage (different device), prompt user
  if (!email) {
    email = window.prompt("Please provide your email for confirmation") || "";
  }

  if (!email) {
    throw new Error("Email is required to complete sign-in");
  }

  try {
    const result = await signInWithEmailLink(auth, email, emailLink);
    // Clear email from storage after successful sign-in
    window.localStorage.removeItem(STORAGE_KEY);

    return result;
  } catch (error: any) {
    throw new Error(error.message || "Failed to complete sign-in");
  }
}

export async function logOut(): Promise<void> {
  const auth = getFirebaseAuth();

  try {
    await signOut(auth);
  } catch (error: any) {
    console.error("Error signing out:", error);
    throw new Error(error.message || "Failed to sign out");
  }
}

type OnAuthStateChangedParams = ArrayTail<
  Parameters<typeof _onAuthStateChanged>
>;

export function onAuthStateChanged(...params: OnAuthStateChangedParams) {
  const auth = getFirebaseAuth();
  return _onAuthStateChanged(auth, ...params);
}

export async function deleteUserFromFirebase() {
  const auth = getFirebaseAuth();

  try {
    await auth?.currentUser?.delete();
  } catch (error: any) {
    console.error("Error deleting user:", error);
    throw new Error(error.message || "Failed to delete user");
  }
}
